import { AccountOrganizerAuthorizationOrder, AccountOrganizerClientSecret } from 'src/models/Account/Organizer';
import {
  AccountOrganizerContributionsResponseType,
  AccountOrganizerGetContributionsParamsType,
  AccountOrganizerTransactionDetailsResponseType,
} from 'src/models/Account/Organizer/contributions';
import {
  AccountOrganizerContributionsChangeAuthorizationOrderRequestType,
  AccountOrganizerContributionsChangePaymentMethodRequestType,
  AccountOrganizerContributionsDeliveredRequestType,
  AccountOrganizerContributionsDonationsExportRequestType,
  AccountOrganizerContributionsListRequestType,
  AccountOrganizerContributionsListType,
  AccountOrganizerContributionsRecurringContributionEditPayPalResponse,
  AccountOrganizerContributionsRecurringContributionEditStripeResponse,
  AccountOrganizerContributionsRecurringContributionsExportRequestType,
  AccountOrganizerContributionsRecurringContributionsListRequestType,
  AccountOrganizerContributionsRecurringContributionsListType,
  AccountOrganizerContributionsRecurringListType,
} from 'src/models/Account/Organizer/contributions/donations';
import {
  AccountOrganizerContributionsLedgerListType,
  AccountOrganizerContributionsLedgerRequestType,
} from 'src/models/Account/Organizer/contributions/ledger';
import {
  AccountOrganizerContributionsSupportersListRequestType,
  AccountOrganizerContributionsSupportersListType,
  AccountOrganizerContributionsSupportersViewResponseType,
} from 'src/models/Account/Organizer/contributions/supporters';
import {
  AccountOrganizerDeleteRegisteredDonorRequestType,
  AccountOrganizerEditRegisteredDonorRequestType,
  AccountOrganizerFindRegisteredDonorRequestType,
  AccountOrganizerFindRegisteredDonorResponseType,
  AccountOrganizerRegisteredDonorsListRequestType,
  AccountOrganizerRegisteredDonorsListType,
  AccountOrganizerRegisteredDonorsViewResponseType,
} from 'src/models/Account/Organizer/registeredDonors';
import { APIExportStartReturnType } from 'src/models/BaseApi';
import { CancelDonationModalRequestType } from 'src/models/Modals/cancelDonation';
import { CancelRecurringDonationResponseType } from 'src/models/Modals/cancelRecurringDonation';
import { FailedReasonResponseType } from 'src/models/Modals/failedReason';
import { RefundReasonModalResponseType } from 'src/models/Modals/refundReason';
import { ViewDetailsModalResponseType } from 'src/models/Modals/viewDetails';
import { ApiFunctionType, httpGetCancellable, httpPost, httpPostCancellable } from 'src/utils/http';

const getPath = (path: string) => `/account/organizer/organizerContributions/${path}`;

export const apiGetContributions: ApiFunctionType<
  AccountOrganizerGetContributionsParamsType,
  AccountOrganizerContributionsResponseType
> = httpGetCancellable(getPath('GetContributions'));

/** contributions */
export const apiContributionsCsv = (
  data: AccountOrganizerContributionsDonationsExportRequestType
): Promise<APIExportStartReturnType> => httpPost(getPath('ContributionsCsv'), data);

export const apiGetOrganizerTransactionDetails = (
  PaymentID: number
): Promise<AccountOrganizerTransactionDetailsResponseType> =>
  httpPost(getPath('viewtransactiondetails'), { PaymentID });

export const apiMarkItemsDelivered: ApiFunctionType<AccountOrganizerContributionsDeliveredRequestType, boolean> =
  httpPostCancellable(getPath('MarkItemsDelivered'));

export const apiUnmarkItemsDelivered: ApiFunctionType<AccountOrganizerContributionsDeliveredRequestType, boolean> =
  httpPostCancellable(getPath('UnmarkItemsDelivered'));

export const apiOrganizerCancelContribution = (
  data: CancelDonationModalRequestType
): Promise<CancelDonationModalRequestType> => httpPost(getPath('cancelpayment'), data);

export const apiLoadContributionRefundReason: ApiFunctionType<{ PaymentID: number }, RefundReasonModalResponseType> =
  httpPostCancellable(getPath('LoadContributionRefundReason'));

export const apiLoadContributionFailedReason = (PaymentID: number): Promise<FailedReasonResponseType> =>
  httpPost(getPath('LoadContributionFailedReason'), { PaymentID });

export const apiCancelRecurringContribution: ApiFunctionType<
  { ContributionID: number },
  CancelRecurringDonationResponseType
> = httpPostCancellable(getPath('CancelRecurringContribution'));

export const apiLoadCancelRecurringContributionDialog: ApiFunctionType<
  { ContributionID: number },
  CancelRecurringDonationResponseType
> = httpPostCancellable(getPath('LoadCancelRecurringContributionDialog'));

export const apiRecurringContributionsCsv = (
  data: AccountOrganizerContributionsRecurringContributionsExportRequestType
): Promise<APIExportStartReturnType> => httpPost(getPath('RecurringContributionsCsv'), data);

export const apiLoadEditRecurringContributionStripeDialog = (
  ContributionID: number
): Promise<AccountOrganizerContributionsRecurringContributionEditStripeResponse> =>
  httpPost(getPath('LoadEditRecurringContributionStripeDialog'), { ContributionID });

export const apiLoadEditRecurringContributionPayPalDialog = (
  ContributionID: number
): Promise<AccountOrganizerContributionsRecurringContributionEditPayPalResponse> =>
  httpPost(getPath('LoadEditRecurringContributionPayPalDialog'), { ContributionID });

export const apiLoadContributions = (
  data: AccountOrganizerContributionsListRequestType
): Promise<AccountOrganizerContributionsListType> => httpPost(getPath('LoadContributions'), data);

export const apiLoadRecurringContributions = (
  data: AccountOrganizerContributionsListRequestType
): Promise<AccountOrganizerContributionsRecurringListType> => httpPost(getPath('LoadRecurringContributions'), data);

export const apiViewContributionsTable = (
  data: AccountOrganizerContributionsListRequestType
): Promise<AccountOrganizerContributionsListType> => httpPost(getPath('ViewContributionsTable'), data);

export const apiViewRecurringContributionsTable = (
  data: AccountOrganizerContributionsRecurringContributionsListRequestType
): Promise<AccountOrganizerContributionsRecurringContributionsListType> =>
  httpPost(getPath('ViewRecurringContributionsTable'), data);
/** end donations */

/** ledger */
export const apiViewLedgerTable: ApiFunctionType<
  AccountOrganizerContributionsLedgerRequestType,
  AccountOrganizerContributionsLedgerListType
> = httpPostCancellable(getPath('ViewLedgerTable'));

export const apiLoadLedgerTable: ApiFunctionType<
  AccountOrganizerContributionsLedgerRequestType,
  AccountOrganizerContributionsLedgerListType
> = httpPostCancellable(getPath('LoadLedgerTable'));

export const apiExportLedgerCsv: ApiFunctionType<{ permanentUrl: string }, APIExportStartReturnType> =
  httpPostCancellable(getPath('LedgersCsv'));

export const apiExportSubLedgerCsv: ApiFunctionType<{ permanentUrl: string }, APIExportStartReturnType> =
  httpPostCancellable(getPath('SubLedgersCsv'));
/** end ledger */

/** registered donors */
export const apiGetOrganizerDonorDetails = (PreRegistrationID: number): Promise<ViewDetailsModalResponseType> =>
  httpPost(getPath('LoadDonorDetails'), { PreRegistrationID });

export const apiLoadRegisteredDonors = (
  data: AccountOrganizerRegisteredDonorsListRequestType
): Promise<AccountOrganizerRegisteredDonorsListType> => httpPost(getPath('LoadRegisteredDonors'), data);

export const apiRegisteredDonorsCsv = (permanentUrl: string): Promise<APIExportStartReturnType> =>
  httpPost(getPath('RegisteredDonorsCsv'), { permanentUrl });

export const apiViewRegisteredDonorsTable = (
  data: AccountOrganizerRegisteredDonorsListRequestType
): Promise<AccountOrganizerRegisteredDonorsViewResponseType> => httpPost(getPath('ViewRegisteredDonorsTable'), data);

export const apiLoadRegisteredDonor: ApiFunctionType<
  AccountOrganizerFindRegisteredDonorRequestType,
  AccountOrganizerFindRegisteredDonorResponseType
> = httpPostCancellable(getPath('LoadPreRegistration'));

export const apiSaveRegisteredDonor: ApiFunctionType<
  AccountOrganizerEditRegisteredDonorRequestType,
  AccountOrganizerEditRegisteredDonorRequestType
> = httpPostCancellable(getPath('EditPreRegistration'));

export const apiDeleteRegisteredDonor: ApiFunctionType<
  AccountOrganizerDeleteRegisteredDonorRequestType,
  AccountOrganizerDeleteRegisteredDonorRequestType
> = httpPostCancellable(getPath('DeletePreRegistration'));
/** end registered donors */

/** supporters */
export const apiLoadSupporters = (
  data: AccountOrganizerContributionsSupportersListRequestType
): Promise<AccountOrganizerContributionsSupportersListType> => httpPost(getPath('LoadSupporters'), data);

export const apiViewSupportersTable = (
  data: AccountOrganizerContributionsSupportersListRequestType
): Promise<AccountOrganizerContributionsSupportersViewResponseType> => httpPost(getPath('ViewSupportersTable'), data);

export const apiSupportersCsv = (permanentUrl: string): Promise<APIExportStartReturnType> =>
  httpPost(getPath('SupportersCsv'), { permanentUrl });
/** end supporters */

export const apiPrepareSetupIntent = (ContributionID: number): Promise<AccountOrganizerClientSecret> =>
  httpPost(getPath('PrepareSetupIntent'), { ContributionID });

export const apiChangePaymentMethod = (
  data: AccountOrganizerContributionsChangePaymentMethodRequestType
): Promise<boolean> => httpPost(getPath('ChangePaymentMethod'), data);

export const PrepareAuthorizationOrder = (ContributionID: number): Promise<AccountOrganizerAuthorizationOrder> =>
  httpPost(getPath('PrepareAuthorizationOrder'), { ContributionID });

export const apiChangeAuthorizationOrder = (
  data: AccountOrganizerContributionsChangeAuthorizationOrderRequestType
): Promise<boolean> => httpPost(getPath('ChangeAuthorizationOrder'), data);
